import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import CustomSelect from '../widgets/CustomSelect';
import RxNormAddWizard from './RxNormAddWizard';
import LinkItemWizard from './LinkItemWizard';
import '../../styles/MedicationCatalog.css';

const API_BASE = `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/medication-catalog`;
const FALLBACK_ROUTES = ['IV', 'IM', 'PO', 'PR', 'S/C', 'PV'];
const FALLBACK_FORMS = [
  'Tablet', 'Capsule', 'Injection', 'Syrup', 'Suspension', 'Infusion',
  'Ampoule', 'Vial', 'Drops', 'Cream', 'Ointment', 'Gel', 'Inhaler',
  'Sachet', 'Powder', 'Solution', 'Suppository', 'Patch',
];

const emptyMedicationForm = { name: '', notes: '', is_active: true };
const emptyPresentationForm = { strength: '', dosage_form: 'Tablet', route: 'PO', is_prescribable: true, is_active: true };
const emptyProductForm = { item_id: '', brand_name: '', is_preferred: false, is_active: true };

const apiError = (error, fallback) => error.response?.data?.error || fallback;

const MedicationCatalog = () => {
  const { token, hasPermission } = useAuth();
  const canView = hasPermission('/medication-catalog', 'View');
  const canAdd = hasPermission('/medication-catalog', 'Add');
  const canEdit = hasPermission('/medication-catalog', 'Edit');
  const canDelete = hasPermission('/medication-catalog', 'Delete');
  const canOverride = hasPermission('/medication-catalog/rxnorm-override', 'Execute');

  const [tab, setTab] = useState('catalog');
  const [routes, setRoutes] = useState(FALLBACK_ROUTES);
  const [dosageForms, setDosageForms] = useState(FALLBACK_FORMS);
  const [medications, setMedications] = useState([]);
  const [medicationDetail, setMedicationDetail] = useState(null);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);
  const [selectedPresentationId, setSelectedPresentationId] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [unmappedQuery, setUnmappedQuery] = useState('');
  const [unmappedItems, setUnmappedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [modal, setModal] = useState(null);
  const [medicationForm, setMedicationForm] = useState(emptyMedicationForm);
  const [presentationForm, setPresentationForm] = useState(emptyPresentationForm);
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const showStatus = (type, text) => setStatus({ type, text });

  const loadMedications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { query: search };
      if (showInactive) params.is_active = 'all';
      const response = await axios.get(`${API_BASE}/medications`, { headers, params });
      setMedications(response.data || []);
    } catch (error) {
      showStatus('error', apiError(error, 'Could not load medications.'));
    } finally {
      setLoading(false);
    }
  }, [token, headers, search, showInactive]);

  const loadMedicationDetail = useCallback(async (medicationId, preferredPresentationId) => {
    if (!token || !medicationId) {
      setMedicationDetail(null);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/medications/${medicationId}`, { headers });
      const detail = response.data;
      setMedicationDetail(detail);
      const nextPresentations = detail.MedicationPresentations || [];
      setSelectedPresentationId((current) => {
        if (preferredPresentationId) return preferredPresentationId;
        if (nextPresentations.some((row) => row.presentation_id === current)) return current;
        return nextPresentations[0]?.presentation_id || null;
      });
    } catch (error) {
      showStatus('error', apiError(error, 'Could not load medication details.'));
    }
  }, [token, headers]);

  const loadUnmapped = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE}/unmapped-items`, {
        headers,
        params: { query: unmappedQuery },
      });
      setUnmappedItems(response.data || []);
    } catch (error) {
      showStatus('error', apiError(error, 'Could not load unmapped Zoho items.'));
    }
  }, [token, headers, unmappedQuery]);

  useEffect(() => {
    if (!token || !canView) return;
    axios.get(`${API_BASE}/options`, { headers })
      .then((response) => {
        if (response.data?.routes?.length) setRoutes(response.data.routes);
        if (response.data?.dosage_forms?.length) setDosageForms(response.data.dosage_forms);
      })
      .catch(() => {});
  }, [token, headers, canView]);

  useEffect(() => {
    if (!canView) return;
    const timer = setTimeout(loadMedications, 250);
    return () => clearTimeout(timer);
  }, [canView, loadMedications]);

  useEffect(() => {
    if (tab === 'unmapped' && canView) {
      const timer = setTimeout(loadUnmapped, 250);
      return () => clearTimeout(timer);
    }
  }, [tab, canView, loadUnmapped]);

  const presentations = useMemo(() => {
    const rows = medicationDetail?.MedicationPresentations || [];
    return showInactive ? rows : rows.filter((row) => row.is_active);
  }, [medicationDetail, showInactive]);

  const selectedPresentation = presentations.find((row) => row.presentation_id === selectedPresentationId) || null;
  const products = useMemo(() => {
    const rows = selectedPresentation?.MedicationProducts || [];
    return showInactive ? rows : rows.filter((row) => row.is_active);
  }, [selectedPresentation, showInactive]);

  const closeModal = () => {
    setModal(null);
    setModalError('');
  };

  const refreshSelected = async (medicationId = selectedMedicationId, presentationId = selectedPresentationId) => {
    await loadMedications();
    if (medicationId) {
      await loadMedicationDetail(medicationId, presentationId);
    }
    if (tab === 'unmapped') {
      await loadUnmapped();
    }
  };

  const selectMedication = async (medicationId) => {
    setSelectedMedicationId(medicationId);
    setSelectedPresentationId(null);
    await loadMedicationDetail(medicationId);
  };

  const openAddMedication = () => setModal({ type: 'rxnorm-medication' });
  const openAddPresentation = () => setModal({ type: 'rxnorm-presentation' });

  const openMedicationModal = (medication) => {
    setMedicationForm(medication ? {
      name: medication.name || '',
      notes: medication.notes || '',
      is_active: medication.is_active !== false,
    } : emptyMedicationForm);
    setModal({ type: 'medication-edit', id: medication.medication_id });
  };

  const openPresentationModal = (presentation) => {
    setPresentationForm(presentation ? {
      strength: presentation.strength || '',
      dosage_form: presentation.dosage_form || 'Tablet',
      route: presentation.route || 'PO',
      is_prescribable: presentation.is_prescribable !== false,
      is_active: presentation.is_active !== false,
    } : emptyPresentationForm);
    setModal(presentation
      ? { type: 'presentation-edit', id: presentation.presentation_id }
      : { type: 'rxnorm-presentation' });
  };

  const openProductModal = (product) => {
    setProductForm(product ? {
      item_id: product.item_id,
      brand_name: product.brand_name || '',
      is_preferred: Boolean(product.is_preferred),
      is_active: product.is_active !== false,
      current_label: product.display_name,
    } : { ...emptyProductForm, is_preferred: products.length === 0 });
    setModal(product ? { type: 'product-edit', id: product.product_id } : { type: 'product-add' });
  };

  const openLinkModal = (item) => {
    setModal({ type: 'link-item', item });
  };

  const saveMedication = async (event) => {
    event.preventDefault();
    setSaving(true);
    setModalError('');
    try {
      await axios.put(`${API_BASE}/medications/${modal.id}`, medicationForm, { headers });
      showStatus('success', 'Medication updated.');
      closeModal();
      await refreshSelected(modal.id, selectedPresentationId);
    } catch (error) {
      setModalError(apiError(error, 'Could not save medication.'));
    } finally {
      setSaving(false);
    }
  };

  const savePresentation = async (event) => {
    event.preventDefault();
    if (!selectedMedicationId) return;
    setSaving(true);
    setModalError('');
    try {
      await axios.put(`${API_BASE}/presentations/${modal.id}`, presentationForm, { headers });
      showStatus('success', 'Presentation updated.');
      closeModal();
      await refreshSelected(selectedMedicationId, modal.id);
    } catch (error) {
      setModalError(apiError(error, 'Could not save presentation.'));
    } finally {
      setSaving(false);
    }
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    if (!selectedPresentationId) return;
    if (modal.type === 'product-add' && !productForm.item_id) {
      setModalError('Select a Zoho item.');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      const payload = {
        brand_name: productForm.brand_name,
        is_preferred: productForm.is_preferred,
        is_active: productForm.is_active,
      };
      if (modal.type === 'product-add') {
        payload.item_id = productForm.item_id;
        const response = await axios.post(
          `${API_BASE}/presentations/${selectedPresentationId}/products`,
          payload,
          { headers }
        );
        showStatus('success', 'Product linked to Zoho item.');
        closeModal();
        await refreshSelected(selectedMedicationId, response.data.presentation?.presentation_id || selectedPresentationId);
      } else {
        if (productForm.item_id) payload.item_id = productForm.item_id;
        await axios.put(`${API_BASE}/products/${modal.id}`, payload, { headers });
        showStatus('success', 'Product updated.');
        closeModal();
        await refreshSelected(selectedMedicationId, selectedPresentationId);
      }
    } catch (error) {
      setModalError(apiError(error, 'Could not save product.'));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (kind, id) => {
    const label = kind === 'medication' ? 'medication' : kind === 'presentation' ? 'presentation' : 'product';
    if (!window.confirm(`Hide this ${label} from the active catalog?`)) return;
    setSaving(true);
    try {
      if (kind === 'medication') {
        await axios.delete(`${API_BASE}/medications/${id}`, { headers });
      } else if (kind === 'presentation') {
        await axios.delete(`${API_BASE}/presentations/${id}`, { headers });
      } else {
        await axios.delete(`${API_BASE}/products/${id}`, { headers });
      }
      showStatus('success', `${label[0].toUpperCase()}${label.slice(1)} hidden.`);
      await refreshSelected();
    } catch (error) {
      showStatus('error', apiError(error, `Could not hide this ${label}.`));
    } finally {
      setSaving(false);
    }
  };

  const setPreferred = async (productId) => {
    if (!selectedPresentationId) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/presentations/${selectedPresentationId}/preferred-product`,
        { product_id: productId },
        { headers }
      );
      showStatus('success', 'Preferred product updated.');
      await refreshSelected(selectedMedicationId, selectedPresentationId);
    } catch (error) {
      showStatus('error', apiError(error, 'Could not set preferred product.'));
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <div className="medication-catalog access-denied">
        <h2>Access Denied</h2>
        <p>You do not have permission to view the medication catalog.</p>
      </div>
    );
  }

  return (
    <div className="medication-catalog">
      <div className="medication-catalog-header">
        <div>
          <h2>Medication Catalog</h2>
          <p className="medication-catalog-subtitle">
            Organize generic drugs, prescribable strengths, and the Zoho products used for billing.
            Current prescribing and pharmacy still use Zoho items; this catalog does not change those screens.
          </p>
        </div>
      </div>

      <div className="medication-catalog-tabs">
        <button
          type="button"
          className={`medication-catalog-tab ${tab === 'catalog' ? 'active' : ''}`}
          onClick={() => setTab('catalog')}
        >
          Catalog
        </button>
        <button
          type="button"
          className={`medication-catalog-tab ${tab === 'unmapped' ? 'active' : ''}`}
          onClick={() => setTab('unmapped')}
        >
          Unmapped Zoho items
        </button>
      </div>

      {status && (
        <div className={`medication-catalog-status ${status.type}`}>{status.text}</div>
      )}

      {tab === 'catalog' && (
        <>
          <div className="medication-catalog-toolbar">
            <input
              type="search"
              placeholder="Search medications"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <label className="medication-catalog-toggle">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(event) => setShowInactive(event.target.checked)}
              />
              Show inactive
            </label>
          </div>

          <div className="medication-catalog-columns">
            <section className="medication-catalog-panel">
              <div className="medication-catalog-panel-header">
                <h3>1. Medication</h3>
                {canAdd && (
                  <button type="button" className="medication-catalog-btn primary" onClick={openAddMedication}>
                    Add
                  </button>
                )}
              </div>
              <div className="medication-catalog-list">
                {loading && <div className="medication-catalog-empty">Loading...</div>}
                {!loading && medications.length === 0 && (
                  <div className="medication-catalog-empty">No medications yet. Search RxNorm to add a generic drug.</div>
                )}
                {medications.map((medication) => (
                  <button
                    key={medication.medication_id}
                    type="button"
                    className={`medication-catalog-row ${selectedMedicationId === medication.medication_id ? 'selected' : ''} ${medication.is_active ? '' : 'inactive'}`}
                    onClick={() => selectMedication(medication.medication_id)}
                  >
                    <span className="medication-catalog-row-title">{medication.name}</span>
                    <span className="medication-catalog-row-meta">
                      {medication.presentation_count || 0} presentation{(medication.presentation_count || 0) === 1 ? '' : 's'}
                      {medication.rxcui ? ` · RxCUI ${medication.rxcui}` : medication.rxnorm_override ? ' · Manual entry' : ''}
                      {!medication.is_active ? ' · Inactive' : ''}
                    </span>
                  </button>
                ))}
              </div>
              {selectedMedicationId && medicationDetail && (
                <div className="medication-catalog-actions">
                  {canEdit && (
                    <button type="button" className="medication-catalog-btn" onClick={() => openMedicationModal(medicationDetail)}>
                      Edit
                    </button>
                  )}
                  {canDelete && medicationDetail.is_active && (
                    <button type="button" className="medication-catalog-btn danger" onClick={() => deactivate('medication', selectedMedicationId)} disabled={saving}>
                      Hide
                    </button>
                  )}
                </div>
              )}
            </section>

            <section className="medication-catalog-panel">
              <div className="medication-catalog-panel-header">
                <h3>2. Presentation</h3>
                {canAdd && selectedMedicationId && (
                  <button type="button" className="medication-catalog-btn primary" onClick={openAddPresentation}>
                    Add
                  </button>
                )}
              </div>
              {!selectedMedicationId && (
                <div className="medication-catalog-hint">Select a medication first.</div>
              )}
              {selectedMedicationId && presentations.length === 0 && (
                <div className="medication-catalog-empty">No presentations yet. Add the strengths this hospital stocks.</div>
              )}
              <div className="medication-catalog-list">
                {presentations.map((presentation) => (
                  <button
                    key={presentation.presentation_id}
                    type="button"
                    className={`medication-catalog-row ${selectedPresentationId === presentation.presentation_id ? 'selected' : ''} ${presentation.is_active ? '' : 'inactive'}`}
                    onClick={() => setSelectedPresentationId(presentation.presentation_id)}
                  >
                    <span className="medication-catalog-row-title">{presentation.label}</span>
                    <span className="medication-catalog-row-meta">
                      Preferred: {presentation.preferred_product_name || 'None'}
                      {presentation.rxcui ? ` · RxCUI ${presentation.rxcui}` : ''}
                    </span>
                    {presentation.is_prescribable ? (
                      <span className="medication-catalog-badge preferred">Prescribable</span>
                    ) : (
                      <span className="medication-catalog-badge warn">Needs preferred product</span>
                    )}
                    {!presentation.is_active && <span className="medication-catalog-badge">Inactive</span>}
                  </button>
                ))}
              </div>
              {selectedPresentation && (
                <div className="medication-catalog-actions">
                  {canEdit && (
                    <button type="button" className="medication-catalog-btn" onClick={() => openPresentationModal(selectedPresentation)}>
                      Edit
                    </button>
                  )}
                  {canDelete && selectedPresentation.is_active && (
                    <button type="button" className="medication-catalog-btn danger" onClick={() => deactivate('presentation', selectedPresentation.presentation_id)} disabled={saving}>
                      Hide
                    </button>
                  )}
                </div>
              )}
            </section>

            <section className="medication-catalog-panel">
              <div className="medication-catalog-panel-header">
                <h3>3. Product</h3>
                {canAdd && selectedPresentation && (
                  <button type="button" className="medication-catalog-btn primary" onClick={() => openProductModal(null)}>
                    Add
                  </button>
                )}
              </div>
              {!selectedPresentation && (
                <div className="medication-catalog-hint">Select a presentation to link Zoho products.</div>
              )}
              {selectedPresentation && products.length === 0 && (
                <div className="medication-catalog-empty">No products yet. Link a Zoho medication item. The first one becomes preferred.</div>
              )}
              <div className="medication-catalog-list">
                {products.map((product) => (
                  <div key={product.product_id} className={`medication-catalog-row ${product.is_active ? '' : 'inactive'}`}>
                    <span className="medication-catalog-row-title">{product.display_name}</span>
                    <span className="medication-catalog-row-meta">
                      Zoho item {product.item_id}
                      {product.Item?.item_status ? ` · ${product.Item.item_status}` : ''}
                    </span>
                    {product.is_preferred && <span className="medication-catalog-badge preferred">Preferred</span>}
                    <div className="medication-catalog-actions" style={{ padding: '8px 0 0', border: 0 }}>
                      {canEdit && !product.is_preferred && product.is_active && (
                        <button type="button" className="medication-catalog-btn" onClick={() => setPreferred(product.product_id)} disabled={saving}>
                          Make preferred
                        </button>
                      )}
                      {canEdit && (
                        <button type="button" className="medication-catalog-btn" onClick={() => openProductModal(product)}>
                          Edit
                        </button>
                      )}
                      {canDelete && product.is_active && (
                        <button type="button" className="medication-catalog-btn danger" onClick={() => deactivate('product', product.product_id)} disabled={saving}>
                          Hide
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </>
      )}

      {tab === 'unmapped' && (
        <>
          <div className="medication-catalog-toolbar">
            <input
              type="search"
              placeholder="Search unmapped Zoho medication items"
              value={unmappedQuery}
              onChange={(event) => setUnmappedQuery(event.target.value)}
            />
          </div>
          <div className="medication-catalog-unmapped-list">
            {unmappedItems.length === 0 && (
              <div className="medication-catalog-empty">
                No unmapped active Zoho medication items match this search.
              </div>
            )}
            {unmappedItems.map((item) => (
              <div key={item.item_id} className="medication-catalog-unmapped-row">
                <div>
                  <span className="medication-catalog-row-title">{item.item_name}</span>
                  <span className="medication-catalog-row-meta">Zoho item {item.item_id}</span>
                </div>
                {canAdd && (
                  <button type="button" className="medication-catalog-btn primary" onClick={() => openLinkModal(item)}>
                    Add to catalog
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {modal && modal.type === 'medication-edit' && (
        <div className="medication-catalog-modal-backdrop" onClick={closeModal}>
          <form className="medication-catalog-modal medication-catalog-form" onClick={(event) => event.stopPropagation()} onSubmit={saveMedication}>
            <h3>Edit medication</h3>
            {modalError && <div className="medication-catalog-status error medication-catalog-modal-error">{modalError}</div>}
            <label>
              Generic name
              <input
                required
                value={medicationForm.name}
                onChange={(event) => setMedicationForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </label>
            <label>
              Notes
              <textarea
                rows={3}
                value={medicationForm.notes}
                onChange={(event) => setMedicationForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </label>
            {modal.type === 'medication-edit' && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={medicationForm.is_active}
                  onChange={(event) => setMedicationForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
                Active
              </label>
            )}
            <div className="medication-catalog-modal-actions">
              <button type="button" className="medication-catalog-btn" onClick={closeModal}>Cancel</button>
              <button type="submit" className="medication-catalog-btn primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {modal && modal.type === 'presentation-edit' && (
        <div className="medication-catalog-modal-backdrop" onClick={closeModal}>
          <form className="medication-catalog-modal medication-catalog-form" onClick={(event) => event.stopPropagation()} onSubmit={savePresentation}>
            <h3>Edit presentation</h3>
            {modalError && <div className="medication-catalog-status error medication-catalog-modal-error">{modalError}</div>}
            <label>
              Strength
              <input
                required
                placeholder="500 mg"
                value={presentationForm.strength}
                onChange={(event) => setPresentationForm((prev) => ({ ...prev, strength: event.target.value }))}
              />
            </label>
            <label>
              Dosage form
              <select
                value={presentationForm.dosage_form}
                onChange={(event) => setPresentationForm((prev) => ({ ...prev, dosage_form: event.target.value }))}
              >
                {dosageForms.map((form) => <option key={form} value={form}>{form}</option>)}
              </select>
            </label>
            <label>
              Route
              <select
                value={presentationForm.route}
                onChange={(event) => setPresentationForm((prev) => ({ ...prev, route: event.target.value }))}
              >
                {routes.map((route) => <option key={route} value={route}>{route}</option>)}
              </select>
            </label>
            {modal.type === 'presentation-edit' && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={presentationForm.is_prescribable}
                  onChange={(event) => setPresentationForm((prev) => ({ ...prev, is_prescribable: event.target.checked }))}
                />
                Prescribable
              </label>
            )}
            {modal.type === 'presentation-edit' && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={presentationForm.is_active}
                  onChange={(event) => setPresentationForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
                Active
              </label>
            )}
            <p className="medication-catalog-hint" style={{ padding: 0 }}>
              A presentation can only be prescribable after it has a preferred product.
            </p>
            <div className="medication-catalog-modal-actions">
              <button type="button" className="medication-catalog-btn" onClick={closeModal}>Cancel</button>
              <button type="submit" className="medication-catalog-btn primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {modal && modal.type === 'rxnorm-medication' && (
        <RxNormAddWizard
          mode="medication"
          canOverride={canOverride}
          headers={headers}
          routes={routes}
          dosageForms={dosageForms}
          onCancel={closeModal}
          onSaved={async (detail) => {
            setSelectedMedicationId(detail.medication_id);
            await refreshSelected(detail.medication_id, detail.MedicationPresentations?.[0]?.presentation_id);
            showStatus('success', 'Medication added from RxNorm.');
            closeModal();
          }}
        />
      )}

      {modal && modal.type === 'rxnorm-presentation' && medicationDetail && (
        <RxNormAddWizard
          mode="presentation"
          medication={medicationDetail}
          existingScdRxcuis={(medicationDetail.MedicationPresentations || []).map((row) => row.rxcui).filter(Boolean)}
          canOverride={canOverride}
          headers={headers}
          routes={routes}
          dosageForms={dosageForms}
          onCancel={closeModal}
          onSaved={async (detail) => {
            const presentationId = detail.presentation_id || detail.MedicationPresentations?.slice(-1)[0]?.presentation_id;
            await refreshSelected(selectedMedicationId, presentationId);
            showStatus('success', 'Presentation added from RxNorm.');
            closeModal();
          }}
        />
      )}

      {modal && (modal.type === 'product-add' || modal.type === 'product-edit') && (
        <div className="medication-catalog-modal-backdrop" onClick={closeModal}>
          <form className="medication-catalog-modal medication-catalog-form" onClick={(event) => event.stopPropagation()} onSubmit={saveProduct}>
            <h3>{modal.type === 'product-add' ? 'Add product' : 'Edit product'}</h3>
            {modalError && <div className="medication-catalog-status error medication-catalog-modal-error">{modalError}</div>}
            <label>
              Zoho item
              {modal.type === 'product-edit' && productForm.current_label && (
                <span className="medication-catalog-row-meta">Current: {productForm.current_label}</span>
              )}
              <CustomSelect
                name="item_id"
                value={productForm.item_id}
                endpoint="/hospital-portal/api/medication-catalog/unmapped-items"
                valueField="item_id"
                labelField="item_name"
                isSearchable
                loadOnMount
                placeholder="Search Zoho medication items"
                onChange={(name, value) => setProductForm((prev) => ({ ...prev, item_id: value }))}
              />
            </label>
            <label>
              Brand name (optional)
              <input
                placeholder="Leave blank for generic"
                value={productForm.brand_name}
                onChange={(event) => setProductForm((prev) => ({ ...prev, brand_name: event.target.value }))}
              />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={productForm.is_preferred}
                onChange={(event) => setProductForm((prev) => ({ ...prev, is_preferred: event.target.checked }))}
              />
              Preferred product
            </label>
            {modal.type === 'product-edit' && (
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={productForm.is_active}
                  onChange={(event) => setProductForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                />
                Active
              </label>
            )}
            <div className="medication-catalog-modal-actions">
              <button type="button" className="medication-catalog-btn" onClick={closeModal}>Cancel</button>
              <button type="submit" className="medication-catalog-btn primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {modal && modal.type === 'link-item' && (
        <LinkItemWizard
          item={modal.item}
          canOverride={canOverride}
          headers={headers}
          routes={routes}
          dosageForms={dosageForms}
          onCancel={closeModal}
          onSaved={async (data) => {
            const medicationId = data.medication?.medication_id;
            const presentationId = data.presentation?.presentation_id;
            setSelectedMedicationId(medicationId);
            setTab('catalog');
            await refreshSelected(medicationId, presentationId);
            showStatus('success', 'Zoho item added to the medication catalog.');
            closeModal();
          }}
        />
      )}
    </div>
  );
};

export default MedicationCatalog;
